import React, { FC, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { getIn, useFormikContext } from 'formik';
import { Button } from '@clearcover/atlas';
import axios from 'axios';

import { useLazyV2Api } from 'Shared/services/Api';
import { Row, Column, FormInput, useToast } from 'Shared/components';
import { combineStrings } from 'Shared/utils';

import { EntityIcon } from 'QuoteApp/components/EntityIcon';
import { formatOptions, getErrorMessage, getFieldErrors } from 'QuoteApp/utils';
import { FormValuesType } from 'QuoteApp/services/endpoints/GetDetails';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';
import { useAppContext } from 'QuoteApp/services/providers';
import { reportEvent } from 'Shared/analytics';
import { VinNotFoundModal } from 'QuoteApp/components/VinNotFoundModal';
import { VehicleMultiselect } from 'QuoteApp/components/VehicleMultiselect';
import { useVehicleMultiselect } from 'QuoteApp/components/VehicleMultiselect/useVehicleMultiselect';

import styles from './styles.module.css';

export type GetVehicleByExactVinResponseType = {
  id: string;
  year: number;
  make: string;
  model: string;
  bodyStyle: string;
  vin?: string;
  partialVin?: string;
};

export type YMMResponseType = {
  years?: number[];
  makes?: string[];
  models?: string[];
  bodyStyles?: Array<{
    bodyStyle: string;
    vins: string[];
  }>;
};

export type BodyStyleResponseType = {
  bodyStyles: Array<{
    bodyStyle: string;
    vins: string[];
  }>;
};

type VehicleSelectionPropsType = {
  vehicleId: string;
};

export const VehicleSelection: FC<VehicleSelectionPropsType> = ({
  vehicleId,
}) => {
  const { details } = useAppContext();

  const [{ loading: vehicleByVinLoading }, executeVehicleByVin] = useLazyV2Api<{
    vehicles: GetVehicleByExactVinResponseType[];
  }>({
    url: '/vehicles/v2/vehicles',
  });

  const { editingId } = useEdit();
  const { serverErrors, alwaysShowErrors, editableFields } = useFieldData();
  const { toast } = useToast();
  const state = details.address[details.principalInsuredId].state.value;
  const configDiscoveryParams = details.configDiscoveryParams;
  const readOnly = editingId !== vehicleId;

  const [{ data: yearsData, loading: yearsLoading }, executeYears] =
    useLazyV2Api<YMMResponseType>({
      url: '/vehicles/v2/years',
    });

  const years = (yearsData?.years || []).map((y) => ({
    label: y.toString(),
    value: y.toString(),
  }));

  const handleYMMBError = useCallback(
    (e: any) => {
      if (axios.isCancel(e)) return;
      setYmmbError(true);
      toast({
        type: 'error',
        message: getErrorMessage(e),
        withSecondaryNav: false,
        icon: 'error',
      });
    },
    [toast]
  );

  // On init, these useEffects load the YMMB values for the drop downs so the values can be changed
  useEffect(() => {
    executeYears({
      params: { configDiscoveryParams },
    })
      .then((res) => checkEmptyYMMBResponse(res.data))
      .catch((e) => handleYMMBError(e));
  }, [executeYears, configDiscoveryParams, handleYMMBError]);

  const [
    { data: vehicleMakesData, loading: vehicleMakesLoading },
    executeMakes,
  ] = useLazyV2Api<YMMResponseType>({ url: '/vehicles/v2/makes' });

  const vehicleMakes = (vehicleMakesData?.makes || []).map((m) => ({
    label: m,
    value: m,
  }));

  const [
    { data: vehicleModelsData, loading: vehicleModelsLoading },
    executeModels,
  ] = useLazyV2Api<YMMResponseType>({ url: '/vehicles/v2/models' });

  const vehicleModels = (vehicleModelsData?.models || []).map((m) => ({
    label: m,
    value: m,
  }));

  const [
    { data: vehicleBodyStylesData, loading: vehicleBodyStylesLoading },
    executeBodyStyles,
  ] = useLazyV2Api<BodyStyleResponseType>({
    url: '/vehicles/v2/body_styles',
  });

  const vehicleBodyStyles = (vehicleBodyStylesData?.bodyStyles || []).map(
    (bs) => ({
      label: bs.bodyStyle,
      value: bs.bodyStyle,
    })
  );

  const {
    handleBlur,
    values,
    touched,
    errors,
    setFieldError,
    setValues,
    setFieldValue,
    initialValues,
  } = useFormikContext<FormValuesType>();

  const {
    multipleVehicleData,
    setMultipleVehicleData,
    isEditingMultiple,
    setIsEditingMultiple,
  } = useVehicleMultiselect();

  const [hasVinError, setHasVinError] = useState<boolean>(false);

  const [showYMMB, setShowYMMB] = useState<boolean>(
    values.vehicles[vehicleId].vin === '' &&
      values.vehicles[vehicleId].year !== ''
  );

  const [hasMatchedVin, setHasMatchedVin] = useState<boolean>(
    values.vehicles[vehicleId].vin !== '' &&
      values.vehicles[vehicleId].vehicleServicesId !== ''
  );

  const [ymmbError, setYmmbError] = useState<boolean>(false);

  const checkEmptyYMMBResponse = (
    data: YMMResponseType | BodyStyleResponseType
  ) => {
    // Check for any of the arrays being empty based on what we expect
    const hasData =
      ('years' in data && data.years && data.years.length > 0) ||
      ('makes' in data && data.makes && data.makes.length > 0) ||
      ('models' in data && data.models && data.models.length > 0) ||
      (data.bodyStyles && data.bodyStyles.length > 0);

    if (!hasData) {
      setYmmbError(true);
      reportEvent('aqui-YMMB-not-found-error');
    }
  };

  // stop editing multiple vehicles if in read only state
  useEffect(() => {
    if (isEditingMultiple && readOnly) {
      setIsEditingMultiple(false);
      setHasMatchedVin(true);
    }
  }, [isEditingMultiple, readOnly, setIsEditingMultiple]);

  // On init, these useEffects load the YMMB values for the drop downs so the values can be changed
  useEffect(() => {
    if (
      values.vehicles[vehicleId].year &&
      !vehicleMakes.length &&
      !vehicleMakesLoading &&
      !ymmbError
    ) {
      executeMakes({
        params: {
          year: values.vehicles[vehicleId].year,
          configDiscoveryParams,
        },
      })
        .then((res) => checkEmptyYMMBResponse(res.data))
        .catch((e) => handleYMMBError(e));
    }
  }, [
    executeMakes,
    values,
    vehicleId,
    vehicleMakes.length,
    vehicleMakesLoading,
    state,
    ymmbError,
    handleYMMBError,
    configDiscoveryParams,
  ]);

  useEffect(() => {
    if (
      values.vehicles[vehicleId].year &&
      values.vehicles[vehicleId].make &&
      !vehicleModels.length &&
      !vehicleModelsLoading &&
      !ymmbError
    ) {
      executeModels({
        params: {
          year: values.vehicles[vehicleId].year,
          make: values.vehicles[vehicleId].make,
          configDiscoveryParams,
        },
      })
        .then((res) => checkEmptyYMMBResponse(res.data))
        .catch((e) => handleYMMBError(e));
    }
  }, [
    executeModels,
    values,
    vehicleId,
    vehicleModels.length,
    vehicleModelsLoading,
    state,
    ymmbError,
    handleYMMBError,
    configDiscoveryParams,
  ]);

  useEffect(() => {
    if (
      values.vehicles[vehicleId].year &&
      values.vehicles[vehicleId].make &&
      values.vehicles[vehicleId].model &&
      !vehicleBodyStyles.length &&
      !vehicleBodyStylesLoading &&
      !ymmbError
    ) {
      executeBodyStyles({
        params: {
          year: values.vehicles[vehicleId].year,
          make: values.vehicles[vehicleId].make,
          model: values.vehicles[vehicleId].model,
          configDiscoveryParams,
        },
      })
        .then((res) => checkEmptyYMMBResponse(res.data))
        .catch((e) => handleYMMBError(e));
    }
  }, [
    executeBodyStyles,
    values,
    vehicleId,
    vehicleBodyStyles.length,
    vehicleBodyStylesLoading,
    state,
    ymmbError,
    handleYMMBError,
    configDiscoveryParams,
  ]);

  const getFieldErrorMessage = (fieldName: string) => {
    return getFieldErrors<FormValuesType>(
      fieldName,
      true,
      alwaysShowErrors,
      errors,
      serverErrors,
      values,
      touched,
      initialValues
    );
  };
  return (
    <>
      <VinNotFoundModal
        open={hasVinError}
        vin={values.vehicles[vehicleId].vin}
        onClose={() => setHasVinError(false)}
      />
      <Row className={styles.vinRow}>
        <Column variant="half">
          <FormInput
            name={`vehicles.${vehicleId}.vin`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setHasMatchedVin(false);
              setIsEditingMultiple(false);
              // Capitalize the VIN and clear other vehicle identification fields
              setValues({
                ...values,
                vehicles: {
                  ...values.vehicles,
                  [vehicleId]: {
                    ...values.vehicles[vehicleId],
                    vin: e.target.value.toUpperCase(),
                    year: '',
                    make: '',
                    model: '',
                    bodyStyle: '',
                    vehicleServicesId: '',
                    partialVin: '',
                  },
                },
              });
            }}
            onBlur={handleBlur}
            value={values.vehicles[vehicleId].vin}
            errorMessage={getFieldErrorMessage(`vehicles.${vehicleId}.vin`)}
            variant={readOnly ? 'readOnly' : 'text'}
            label="VIN (Vehicle Identification Number)"
            data-testid="vehicle-vin"
            disabled={
              readOnly || !getIn(editableFields, `vehicles.${vehicleId}.vin`)
            }
          />
        </Column>
        <Column variant="half">
          {!readOnly && (
            <Button
              className={styles.vinLookupButton}
              variant="primary"
              isSubmitting={vehicleByVinLoading}
              data-testid="lookup-vin-button"
              disabled={
                readOnly ||
                hasMatchedVin ||
                isEditingMultiple ||
                !!getFieldErrorMessage(`vehicles.${vehicleId}.vin`) ||
                !getIn(editableFields, `vehicles.${vehicleId}.vin`)
              }
              onClick={() => {
                if (values.vehicles[vehicleId].vin) {
                  executeVehicleByVin({
                    params: {
                      vin: values.vehicles[vehicleId].vin,
                      configDiscoveryParams,
                      exact_match: true,
                    },
                  })
                    .then(({ data }) => {
                      // reset any existing submission error
                      setFieldError(
                        `vehicles.${editingId}.submissionError`,
                        undefined
                      );
                      setHasVinError(false);

                      const vehicles = data.vehicles || [];

                      if (!vehicles.length) {
                        throw new Error('VIN not found');
                      } else if (vehicles.length === 1) {
                        setHasMatchedVin(true);
                        setValues({
                          ...values,
                          vehicles: {
                            ...values.vehicles,
                            [vehicleId]: {
                              ...values.vehicles[vehicleId],
                              year: vehicles[0].year.toString(),
                              make: vehicles[0].make,
                              model: vehicles[0].model,
                              bodyStyle: vehicles[0].bodyStyle,
                              vehicleServicesId: vehicles[0].id,
                              partialVin: vehicles[0].partialVin || '',
                            },
                          },
                        });
                      } else {
                        // multiple vin radio selection
                        setIsEditingMultiple(true);
                        setMultipleVehicleData(vehicles);
                      }
                    })
                    .catch(() => {
                      setHasVinError(true);
                      setFieldError(
                        `vehicles.${vehicleId}.vin`,
                        'This vehicle is not in our system. Please verify the VIN and try again.'
                      );
                    })
                    .finally(() => {
                      // reset the YMMB value after request
                      setShowYMMB(false);
                    });
                }
              }}
            >
              Lookup VIN
            </Button>
          )}
        </Column>
      </Row>
      {isEditingMultiple ? (
        <Row>
          <Column>
            <VehicleMultiselect
              layout="columns"
              data={multipleVehicleData}
              vehicleId={vehicleId}
              hasError={getIn(errors, `vehicles.${vehicleId}.submissionError`)}
            />
          </Column>
        </Row>
      ) : (
        <Row>
          {hasMatchedVin && (
            <div className={styles.matchContainer}>
              <EntityIcon variant="vehicle" />
              <div className={styles.vehicleFoundWrapper}>
                <div
                  className={classNames(styles.vehicleFoundDisplay, 'fs-mask')}
                >
                  {combineStrings([
                    values.vehicles[vehicleId].year,
                    values.vehicles[vehicleId].make,
                    values.vehicles[vehicleId].model,
                  ])}
                </div>
                <div className={styles.vehicleFoundBody}>
                  {`Body Style: ${values.vehicles[vehicleId].bodyStyle}`}
                </div>
              </div>
            </div>
          )}
          {!hasMatchedVin && showYMMB && (
            <>
              <Column variant="quarter">
                <FormInput
                  name={`${vehicleId}.year`}
                  onChange={(value: string) => {
                    setValues({
                      ...values,
                      vehicles: {
                        ...values.vehicles,
                        [vehicleId]: {
                          ...values.vehicles[vehicleId],
                          year: value,
                          make: '',
                          model: '',
                          bodyStyle: '',
                        },
                      },
                    });
                    setYmmbError(false);
                    executeMakes({
                      params: { year: value, configDiscoveryParams },
                    })
                      .then((res) => checkEmptyYMMBResponse(res.data))
                      .catch((e) => handleYMMBError(e));
                  }}
                  options={formatOptions(years)}
                  optionsSortFn={(a: string, b: string) =>
                    // Sort years in reverse order
                    parseInt(a) < parseInt(b) ? 1 : -1
                  }
                  onBlur={handleBlur}
                  value={values.vehicles[vehicleId].year}
                  disabled={
                    yearsLoading ||
                    hasMatchedVin ||
                    readOnly ||
                    !getIn(editableFields, `vehicles.${vehicleId}.year`)
                  }
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  label="Year"
                  data-testid="vehicle-year"
                  errorMessage={getFieldErrorMessage(
                    `vehicles.${vehicleId}.year`
                  )}
                  loading={yearsLoading}
                />
              </Column>
              <Column variant="quarter">
                <FormInput
                  name={`${vehicleId}.make`}
                  onChange={(value: string) => {
                    setValues({
                      ...values,
                      vehicles: {
                        ...values.vehicles,
                        [vehicleId]: {
                          ...values.vehicles[vehicleId],
                          make: value,
                          model: '',
                          bodyStyle: '',
                        },
                      },
                    });
                    setYmmbError(false);
                    executeModels({
                      params: {
                        year: values.vehicles[vehicleId].year,
                        make: value,
                        configDiscoveryParams,
                      },
                    })
                      .then((res) => checkEmptyYMMBResponse(res.data))
                      .catch(handleYMMBError);
                  }}
                  disabled={
                    values.vehicles[vehicleId].year === '' ||
                    hasMatchedVin ||
                    readOnly ||
                    !getIn(editableFields, `vehicles.${vehicleId}.make`)
                  }
                  loading={vehicleMakesLoading}
                  options={formatOptions(vehicleMakes)}
                  onBlur={handleBlur}
                  value={values.vehicles[vehicleId].make}
                  errorMessage={getFieldErrorMessage(
                    `vehicles.${vehicleId}.make`
                  )}
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  label="Make"
                  data-testid="vehicle-make"
                />
              </Column>
              <Column variant="quarter">
                <FormInput
                  name={`${vehicleId}.model`}
                  onChange={(value: string) => {
                    setValues({
                      ...values,
                      vehicles: {
                        ...values.vehicles,
                        [vehicleId]: {
                          ...values.vehicles[vehicleId],
                          model: value,
                          bodyStyle: '',
                        },
                      },
                    });
                    setYmmbError(false);
                    executeBodyStyles({
                      params: {
                        year: values.vehicles[vehicleId].year,
                        make: values.vehicles[vehicleId].make,
                        model: value,
                        configDiscoveryParams,
                      },
                    })
                      .then((res) => checkEmptyYMMBResponse(res.data))
                      .catch(handleYMMBError);
                  }}
                  disabled={
                    values.vehicles[vehicleId].make === '' ||
                    hasMatchedVin ||
                    readOnly ||
                    !getIn(editableFields, `vehicles.${vehicleId}.model`)
                  }
                  loading={vehicleModelsLoading}
                  options={formatOptions(vehicleModels)}
                  onBlur={handleBlur}
                  value={values.vehicles[vehicleId].model}
                  errorMessage={getFieldErrorMessage(
                    `vehicles.${vehicleId}.model`
                  )}
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  label="Model"
                  data-testid="vehicle-model"
                />
              </Column>
              <Column variant="quarter">
                <FormInput
                  name={`${vehicleId}.bodyStyle`}
                  onChange={(value: string) => {
                    setValues({
                      ...values,
                      vehicles: {
                        ...values.vehicles,
                        [vehicleId]: {
                          ...values.vehicles[vehicleId],
                          bodyStyle: value,
                          vehicleServicesId: '',
                          partialVin: '',
                        },
                      },
                    });

                    // Fetch the vehicle ID since we only have body style string now
                    executeVehicleByVin({
                      params: {
                        year: values.vehicles[vehicleId].year,
                        make: values.vehicles[vehicleId].make,
                        model: values.vehicles[vehicleId].model,
                        bodyStyle: value,
                        configDiscoveryParams,
                      },
                    })
                      .then(({ data }) => {
                        if (data.vehicles && data.vehicles.length > 0) {
                          const vehicle = data.vehicles[0];
                          setFieldValue(
                            `vehicles.${vehicleId}.vehicleServicesId`,
                            vehicle.id
                          );
                          setFieldValue(
                            `vehicles.${vehicleId}.partialVin`,
                            vehicle.partialVin || ''
                          );
                        }
                      })
                      .catch((e) => handleYMMBError(e));
                  }}
                  disabled={
                    values.vehicles[vehicleId].model === '' ||
                    hasMatchedVin ||
                    readOnly ||
                    !getIn(editableFields, `vehicles.${vehicleId}.bodyStyle`)
                  }
                  loading={vehicleBodyStylesLoading}
                  options={formatOptions(vehicleBodyStyles)}
                  onBlur={handleBlur}
                  value={values.vehicles[vehicleId].bodyStyle}
                  errorMessage={getFieldErrorMessage(
                    `vehicles.${vehicleId}.bodyStyle`
                  )}
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  label="Body Style"
                  data-testid="vehicle-body"
                />
              </Column>
            </>
          )}
          {!hasMatchedVin && !showYMMB && (
            <Button
              className={styles.linkBtn}
              onClick={() => {
                setShowYMMB(true);

                // clear vin value when showing YMMB
                setValues({
                  ...values,
                  vehicles: {
                    ...values.vehicles,
                    [vehicleId]: {
                      ...values.vehicles[vehicleId],
                      vin: '',
                    },
                  },
                });
              }}
              link
              disabled={
                readOnly ||
                !getIn(editableFields, `vehicles.${vehicleId}.year`) ||
                !getIn(editableFields, `vehicles.${vehicleId}.make`) ||
                !getIn(editableFields, `vehicles.${vehicleId}.model`) ||
                !getIn(editableFields, `vehicles.${vehicleId}.bodyStyle`)
              }
            >
              Add by year, make, model
            </Button>
          )}
        </Row>
      )}
    </>
  );
};
